import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";

import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Radio, Search, Trash2 } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/settings/channels")({
	component: ChannelsPage,
});

const client = createClient(CatalogService, transport);

function ChannelsPage() {
	const qc = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);

	const { data, isLoading, error } = useQuery({
		queryKey: ["sales-channels"],
		queryFn: () => client.listSalesChannels({}),
	});
	const channels = data?.channels ?? [];
	const [deleteId, setDeleteId] = useState<string | null>(null);

	const toggleMut = useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			client.updateSalesChannel({ id, isActive }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["sales-channels"] }),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteSalesChannel({ id }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["sales-channels"] }),
	});

	const createMut = useMutation({
		mutationFn: ({ name, slug }: { name: string; slug: string }) =>
			client.createSalesChannel({ name, slug }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["sales-channels"] });
			setShowCreate(false);
		},
	});

	const [newName, setNewName] = useState("");
	const [newSlug, setNewSlug] = useState("");
	const [search, setSearch] = useState("");

	const filtered = channels.filter(
		(ch) =>
			!search ||
			ch.name.toLowerCase().includes(search.toLowerCase()) ||
			ch.slug.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Поиск каналов…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Dialog
					open={showCreate}
					onOpenChange={(open) => {
						setShowCreate(open);
						if (!open) {
							setNewName("");
							setNewSlug("");
						}
					}}
				>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="h-4 w-4 mr-1" />
							Добавить канал
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Новый канал продаж</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="space-y-2">
								<Label>Название</Label>
								<Input
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
									placeholder="Например: Avito"
								/>
							</div>
							<div className="space-y-2">
								<Label>Идентификатор</Label>
								<Input
									value={newSlug}
									onChange={(e) => setNewSlug(e.target.value)}
									placeholder="avito"
								/>
								<p className="text-xs text-muted-foreground">
									Используется в API
								</p>
							</div>
						</div>
						<DialogFooter>
							<Button
								disabled={
									!newName.trim() || !newSlug.trim() || createMut.isPending
								}
								onClick={() =>
									createMut.mutate({
										name: newName.trim(),
										slug: newSlug.trim(),
									})
								}
							>
								{createMut.isPending && (
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								)}
								Создать
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{isLoading && (
				<div className="flex justify-center py-12">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			)}

			{error && (
				<Card className="px-4 py-6 flex items-center gap-3 text-destructive">
					<span className="text-sm">{String(error)}</span>
				</Card>
			)}

			{!isLoading && !error && filtered.length === 0 && (
				<Card className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
					<Radio className="h-8 w-8" />
					<span className="text-sm">Нет настроенных каналов</span>
				</Card>
			)}

			<div className="space-y-2">
				{filtered.map((ch) => (
					<Card key={ch.id} className="px-4 py-3 flex items-center gap-3">
						<Switch
							checked={ch.isActive}
							onCheckedChange={(v) =>
								toggleMut.mutate({ id: ch.id, isActive: v })
							}
						/>
						{ch.color && (
							<div
								className="h-3 w-3 rounded-full shrink-0"
								style={{ backgroundColor: ch.color }}
							/>
						)}
						<div className="flex-1 min-w-0">
							<span className="text-sm font-medium">{ch.name}</span>
							<span className="text-xs text-muted-foreground ml-2">
								{ch.slug}
							</span>
						</div>
						{ch.isActive ? (
							<Badge variant="default" className="text-[10px]">
								Активен
							</Badge>
						) : (
							<Badge variant="secondary" className="text-[10px]">
								Выключен
							</Badge>
						)}
						<Button
							variant="ghost"
							size="sm"
							className="text-destructive"
							onClick={() => setDeleteId(ch.id)}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</Card>
				))}
			</div>

			<AlertDialog
				open={!!deleteId}
				onOpenChange={(open) => {
					if (!open) setDeleteId(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Удалить канал?</AlertDialogTitle>
						<AlertDialogDescription>
							Это действие нельзя отменить.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Отмена</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={() => {
								if (deleteId) {
									deleteMut.mutate(deleteId);
									setDeleteId(null);
								}
							}}
						>
							Удалить
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
